import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Sparkles, Lightbulb, ShieldCheck, Check, Download, LogIn } from "lucide-react";
import { businessApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GenerationStatus } from "@/components/business/GenerationStatus";

const SID_KEY = "promptivo.biz.sessionId";
const GEN_STAGES = ["Analizuję Twoje odpowiedzi", "Tworzę kierunki biznesowe", "Obliczam scoring", "Przygotowuję ranking TOP 3", "Buduję plan walidacji"];
const scoreColor = (s) => (s >= 70 ? "#16a34a" : s >= 45 ? "#ca8a04" : "#dc2626");

export default function BizGenerator() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [sid, setSid] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [rejected, setRejected] = useState([]);
  const [locked, setLocked] = useState(false);
  const [clarify, setClarify] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const saveTimer = useRef(null);
  const hydrated = useRef(false);

  // Init: config + session (resume or create).
  useEffect(() => {
    (async () => {
      try {
        const c = await businessApi.config();
        setCfg(c);
        if (user) { try { await businessApi.claim(); } catch { /* ignore */ } }
        let id = null;
        try { id = localStorage.getItem(SID_KEY); } catch { /* ignore */ }
        if (id) {
          try {
            const s = await businessApi.getSession(id);
            setSid(s.id); setAnswers(s.answers || {}); setStep(s.current_step || 0);
            if (s.candidates?.length) { setCandidates(s.candidates); setLocked(!!s.locked); setSelectedId(s.selected_candidate_id); setRejected(s.rejected || []); }
            hydrated.current = true;
            return;
          } catch { /* stale id */ }
        }
        const s = await businessApi.createSession({});
        setSid(s.id);
        try { localStorage.setItem(SID_KEY, s.id); } catch { /* ignore */ }
        hydrated.current = true;
      } catch {
        toast.error("Nie udało się wczytać Generatora. Odśwież stronę.");
      }
    })();
  }, [user]);

  // Autosave (debounce).
  const scheduleSave = useCallback((next, nextStep) => {
    if (!sid) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      businessApi.patchSession(sid, { answers: next, currentStep: nextStep }).catch(() => {});
    }, 800);
  }, [sid]);

  const setField = (id, value) => {
    setAnswers((prev) => { const next = { ...prev, [id]: value }; scheduleSave(next, step); return next; });
  };

  const goStep = (n) => { setStep(n); scheduleSave(answers, n); };

  const steps = cfg?.steps || [];
  const isSummary = step === steps.length - 1;

  const generate = async () => {
    setGenerating(true); setClarify([]);
    try {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      await businessApi.patchSession(sid, { answers, currentStep: step }).catch(() => {});
      const d = await businessApi.generate(sid);
      if (d.needsClarification) { setClarify(d.questions || []); toast.message("Doprecyzuj jedną rzecz, aby uzyskać lepsze pomysły."); return; }
      setCandidates(d.candidates || []); setLocked(!!d.locked); setRejected(d.rejected || []);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (e?.response?.status === 422 && msg?.missing) { toast.error("Uzupełnij wymagane pola (Punkt startu, Budżet)."); return; }
      const timeoutish = !e?.response || e?.code === "ECONNABORTED" || [502, 503, 504].includes(e?.response?.status);
      toast.error(
        timeoutish
          ? "Generowanie trwało zbyt długo lub łącze zawiodło. Twoje odpowiedzi są zapisane — spróbuj ponownie."
          : (typeof msg === "string" ? msg : "Nie udało się wygenerować pomysłów. Spróbuj ponownie."),
        { action: { label: "Spróbuj ponownie", onClick: () => generate() } },
      );
    } finally { setGenerating(false); }
  };

  const selectIdea = async (id) => {
    if (!user) { navigate(`/rejestracja?returnTo=${encodeURIComponent("/biznes/generator")}`); return; }
    try { await businessApi.select(sid, id); setSelectedId(id); toast.success("Pomysł zapisany w projekcie."); }
    catch { toast.error("Nie udało się zapisać wyboru."); }
  };

  const download = (fmt) => { window.open(businessApi.exportUrl(sid, fmt), "_blank"); };

  if (!cfg) return <div className="flex min-h-screen items-center justify-center bg-brand-bg"><Loader2 className="animate-spin text-brand-accent" /></div>;

  return (
    <div className="min-h-screen bg-brand-bg text-brand-text">
      <header className="mx-auto flex max-w-content items-center justify-between px-4 py-5 md:px-8">
        <Link to="/biznes" className="inline-flex items-center gap-2 text-sm text-brand-sec hover:text-brand-text"><ArrowLeft size={16} /> Biznes i pomysły</Link>
        {user === false && <Link to="/logowanie" className="inline-flex items-center gap-1.5 text-sm text-brand-sec hover:text-brand-text"><LogIn size={15} /> Zaloguj się</Link>}
      </header>

      <main className="mx-auto max-w-3xl px-4 pb-28 md:px-8">
        {!candidates ? (
          <>
            <div className="mb-6">
              <p className="text-[13px] font-semibold text-brand-accent">Krok {step + 1} z {steps.length} · {steps[step].title}</p>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-brand-elevated">
                <div className="h-full rounded-full bg-brand-accent transition-all" style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
              </div>
            </div>

            <h1 className="font-display text-2xl font-semibold sm:text-3xl" data-testid="biz-step-title">{steps[step].title}</h1>
            {steps[step].desc && <p className="mt-2 text-brand-sec">{steps[step].desc}</p>}

            {clarify.length > 0 && (
              <div className="mt-4 rounded-panel border border-amber-500/40 bg-amber-500/10 p-4 text-[13px] text-amber-200" data-testid="biz-clarify">
                <p className="font-semibold">Doprecyzuj:</p>
                <ul className="mt-1 list-disc pl-5">{clarify.map((q, i) => <li key={i}>{q}</li>)}</ul>
              </div>
            )}

            {!isSummary ? (
              <div className="mt-6 space-y-6" data-testid="biz-step-fields">
                {steps[step].fields.map((f) => <FieldInput key={f.id} f={f} value={answers[f.id]} onChange={(v) => setField(f.id, v)} />)}
              </div>
            ) : (
              <div className="mt-6 rounded-panel border border-brand-line bg-brand-surface p-5" data-testid="biz-summary">
                <p className="text-sm text-brand-sec">Sprawdź odpowiedzi i wygeneruj propozycje. Możesz cofnąć się i poprawić dowolny krok.</p>
                <ul className="mt-3 space-y-1.5 text-[13.5px]">
                  {steps.slice(0, -1).map((st) => {
                    const filled = st.fields.filter((f) => { const v = answers[f.id]; return !(v === undefined || v === "" || v === false || (Array.isArray(v) && !v.length)); }).length;
                    return <li key={st.id} className="flex items-center justify-between border-b border-brand-line/60 pb-1.5"><span>{st.title}</span><span className="text-brand-muted">{filled} odp.</span></li>;
                  })}
                </ul>
                <button type="button" data-testid="biz-generate" disabled={generating} onClick={generate} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-button bg-brand-accent px-6 py-3 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                  {generating ? <Loader2 size={17} className="animate-spin" /> : <Sparkles size={16} />} {generating ? "Generuję pomysły…" : "Wygeneruj 3 pomysły"}
                </button>
                <GenerationStatus stages={GEN_STAGES} active={generating} className="mt-4" />
                {cfg.warning && <p className="mt-2 text-center text-[12px] text-brand-muted">{cfg.warning}</p>}
              </div>
            )}

            <div className="mt-8 flex items-center justify-between">
              <button type="button" data-testid="biz-back" disabled={step === 0} onClick={() => goStep(step - 1)} className="inline-flex items-center gap-2 rounded-button border border-brand-line px-4 py-2.5 text-sm font-semibold disabled:opacity-40"><ArrowLeft size={15} /> Wstecz</button>
              {!isSummary && <button type="button" data-testid="biz-next" onClick={() => goStep(step + 1)} className="inline-flex items-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on"><span>Dalej</span> <ArrowRight size={15} /></button>}
            </div>
          </>
        ) : (
          <div data-testid="biz-results">
            <button type="button" onClick={() => setCandidates(null)} className="mb-4 inline-flex items-center gap-2 text-sm text-brand-sec hover:text-brand-text"><ArrowLeft size={15} /> Wróć do wywiadu</button>
            <h1 className="font-display text-2xl font-semibold sm:text-3xl">Twoje pomysły ({candidates.length})</h1>
            <p className="mt-2 text-[13px] text-brand-muted">{cfg.warning}</p>

            {locked && (
              <div className="mt-4 rounded-panel border border-brand-accent/40 bg-brand-accent/10 p-4" data-testid="biz-gate">
                <p className="text-sm text-brand-text">To skrócony podgląd. Załóż bezpłatne konto, aby zobaczyć pełne uzasadnienia, koszt startu, plan walidacji 7 dni i zapisać projekt.</p>
                <Link to={`/rejestracja?returnTo=${encodeURIComponent("/biznes/generator")}`} className="mt-3 inline-flex items-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on" data-testid="biz-gate-register"><Lightbulb size={16} /> Załóż konto i odblokuj pełne wyniki</Link>
              </div>
            )}

            {!locked && (
              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" data-testid="biz-export-pdf" onClick={() => download("pdf")} className="inline-flex items-center gap-2 rounded-button border border-brand-line px-4 py-2 text-[13px] font-semibold hover:border-brand-accent/60"><Download size={15} /> PDF</button>
                <button type="button" data-testid="biz-export-docx" onClick={() => download("docx")} className="inline-flex items-center gap-2 rounded-button border border-brand-line px-4 py-2 text-[13px] font-semibold hover:border-brand-accent/60"><Download size={15} /> DOCX</button>
              </div>
            )}

            <div className="mt-6 space-y-4">
              {candidates.map((c) => <Candidate key={c.id} c={c} locked={locked} selected={selectedId === c.id} onSelect={() => selectIdea(c.id)} labels={cfg.scoreLabels} />)}
            </div>

            {!locked && rejected.length > 0 && (
              <details className="mt-6 rounded-panel border border-brand-line bg-brand-surface/60 p-5" data-testid="biz-rejected">
                <summary className="cursor-pointer font-display text-base font-semibold text-brand-sec">Odrzucone pomysły ({rejected.length}) — dlaczego odpadły</summary>
                <div className="mt-4 space-y-3">
                  {rejected.map((r) => (
                    <div key={r.id} className="rounded-field border border-brand-line/60 bg-brand-bg p-3" data-testid="biz-rejected-item">
                      <div className="flex items-center gap-2">
                        <span className="rounded-full bg-brand-elevated px-2 py-0.5 text-[12px] font-bold text-brand-muted">{r.score}/100</span>
                        <span className="font-semibold text-brand-text">{r.title}</span>
                      </div>
                      {r.summary && <p className="mt-1 text-[13px] text-brand-sec">{r.summary}</p>}
                      <p className="mt-1 text-[13px] text-brand-muted"><strong>Powód odrzucenia:</strong> {r.reason}</p>
                      {r.usedData && <p className="text-[12px] text-brand-muted">Wynika z: {r.usedData}</p>}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function FieldInput({ f, value, onChange }) {
  const base = "w-full rounded-field border border-brand-line bg-brand-bg px-4 py-2.5 text-[15px] text-brand-text outline-none focus:border-brand-accent";
  return (
    <div data-testid={`biz-field-${f.id}`}>
      <label className="mb-2 block text-sm font-medium text-brand-text">{f.label}{f.required && <span className="text-brand-accent"> *</span>}</label>
      {f.type === "single" && (
        <div className="flex flex-wrap gap-2">
          {f.options.map((opt) => (
            <button key={opt} type="button" data-testid={`biz-opt-${f.id}-${opt}`} onClick={() => onChange(value === opt ? "" : opt)}
              className={`rounded-full border px-3.5 py-2 text-[13px] font-medium transition-colors ${value === opt ? "border-brand-accent bg-brand-accent/10 text-brand-text" : "border-brand-line text-brand-sec hover:text-brand-text"}`}>{opt}</button>
          ))}
        </div>
      )}
      {f.type === "text" && <input className={base} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder || ""} />}
      {f.type === "textarea" && <textarea rows={3} className={base} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder || ""} />}
      {f.type === "checkbox" && (
        <label className="inline-flex cursor-pointer items-center gap-2 text-[14px] text-brand-text">
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-[color:var(--brand-accent,#b6f500)]" /> {f.label}
        </label>
      )}
    </div>
  );
}

function Candidate({ c, locked, selected, onSelect, labels }) {
  return (
    <div className="rounded-panel border border-brand-line bg-brand-surface p-5" data-testid="biz-candidate">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-[13px] font-bold text-white" style={{ background: scoreColor(c.score) }}>{c.score}</div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-lg font-semibold">{c.title}</h3>
          <p className="mt-1 text-[14px] text-brand-sec">{c.summary}</p>
          {c.forWhom && <p className="mt-1 text-[13px] text-brand-muted"><strong>Dla kogo:</strong> {c.forWhom}</p>}
        </div>
      </div>
      {!locked && (
        <div className="mt-4 grid gap-3 text-[13.5px] sm:grid-cols-2">
          {c.reasons?.length > 0 && <Info label="Dlaczego pasuje">{c.reasons.map((r, i) => <li key={i}>{r}</li>)}</Info>}
          {c.validationPlan?.length > 0 && <Info label="Plan walidacji (7 dni)">{c.validationPlan.map((p, i) => <li key={i}>{p}</li>)}</Info>}
          <KV label="Model" v={c.model} /><KV label="Koszt startu" v={c.startCost} />
          <KV label="Czas do testu" v={c.timeToTest} /><KV label="Przewaga" v={c.advantage} />
          <KV label="Ryzyko" v={c.risk} /><KV label="Pierwszy test" v={c.firstTest} />
          <KV label="Jak zarabia" v={c.monetization} />
        </div>
      )}
      {!locked && c.scoreBreakdown && (
        <p className="mt-3 text-[12px] text-brand-muted">{Object.entries(c.scoreBreakdown).map(([k, v]) => `${labels?.[k] || k}: ${v}/10`).join(" · ")}. {c.scoreRationale}</p>
      )}
      <button type="button" data-testid={`biz-select-${c.id}`} onClick={onSelect} className={`mt-4 inline-flex items-center gap-2 rounded-button px-5 py-2.5 text-sm font-semibold transition-transform hover:-translate-y-0.5 ${selected ? "border border-brand-accent text-brand-accent" : "bg-brand-accent text-brand-on"}`}>
        {selected ? <><Check size={16} /> Wybrany</> : <>{locked ? "Załóż konto, aby wybrać" : "Wybierz ten pomysł"} <ArrowRight size={15} /></>}
      </button>
    </div>
  );
}

const Info = ({ label, children }) => (<div><p className="mb-1 font-semibold text-brand-text">{label}</p><ul className="list-disc pl-5 text-brand-sec">{children}</ul></div>);
const KV = ({ label, v }) => v ? (<p><span className="font-semibold text-brand-text">{label}:</span> <span className="text-brand-sec">{v}</span></p>) : null;
